import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { CenterFrameLayout } from 'Layouts';
import { fetchSubscriptions, fetchSubscriptionStatus } from 'State/Subscriptions';
import { verifyGiftCodeV1, redeemGiftV1, verifyGiftCodeV2, redeemGiftV2 } from 'Api/AccountApi';
import { getEntitlements } from 'State/User';
import { onSubmitReactFormError } from 'Utils/FormValidation';
import { GIFTING_QUERY_PARAM } from 'Utils/Constants';
import { GiftCardInfo } from 'Features/GiftCardInfo';
import { SmartLink } from 'Components/SmartLink';
import RedeemGiftCardForm from 'Forms/RedeemGiftCardForm';
import { ShippingForm } from 'Forms/ShippingForm';
import ZipCodeForm from 'Forms/ZipCodeForm';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { RedeemGiftFinePrint, RedeemGiftTrialistFinePrint, RedeemGiftIAPFinePrint } from 'Components/FinePrint';
import giftPlans from 'Config/GiftPlans.json';
import { useStore } from 'Hooks/useStore';
import './RedeemGiftCardContainer.css';
import {
  canEvaluateUserUnmigrated,
  isUserUnmigrated as isUserUnmigratedFunc,
  redirectToFoxoneSubsPath,
} from 'Utils/FoxoneSubs';

export const RedeemGiftCardContainer = () => {
  const navigate = useNavigate();
  const [giftData, setGiftData] = useState(null);
  const [giftCode, setGiftCode] = useState(null);
  const [showShippingForm, setShippingForm] = useState(false);
  const [showZipCodeForm, setZipCodeForm] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [zipCodeFormValues, setZipcodeFormValues] = useState({});
  const [isInitialized, setInitialized] = useState(false);
  const {
    routes: { signin, giftCardRedeemConfirmation },
    defaultConfig: { rootDomain },
  } = useStore('appConfig');
  const { isAuthenticated } = useStore('user');
  const subscription = useStore('subscription');
  const { isIAPUser, isTrialistUser } = subscription;

  const foxoneSubsEvaluationReady = isInitialized && canEvaluateUserUnmigrated({ subscription, isAuthenticated });
  const isUserUnmigrated = foxoneSubsEvaluationReady && isUserUnmigratedFunc({ subscription });
  const showPageLoader = !foxoneSubsEvaluationReady;

  useEffect(() => {
    setShippingForm(giftData && giftData.hasMerchandise);
    setZipCodeForm(giftData && !giftData.hasMerchandise);
  }, [giftData]);

  useEffect(() => {
    fetchSubscriptions();
    fetchSubscriptionStatus();
    setInitialized(true);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      trackEvent(TRACKING_ENUMS.EVENT_REDEEM_STARTED);
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <Navigate replace to={`${signin}?${GIFTING_QUERY_PARAM}`} />;
  }

  const handleGiftCodeSubmit = async ({ giftCode }) => {
    try {
      const giftData = await verifyGiftCode(giftCode, isUserUnmigrated);
      if (giftData?.valid) {
        const { analytics } = giftData;
        setGiftData(giftData);
        setGiftCode(giftCode);
        trackEvent(TRACKING_ENUMS.EVENT_REDEEM_APPLIED, {
          page_plan_name: analytics?.planName,
          page_plan_price: analytics?.planPrice,
          page_plan_id: analytics?.planId,
        });
      }
    } catch (error) {
      trackEvent(TRACKING_ENUMS.EVENT_REDEEM_ERROR, {
        page_error_description: error,
        page_error_type: 'server-side',
      });
      return onSubmitReactFormError(error || 'Please enter a valid gift PIN code.');
    }
  };

  const onSubmitToRedeem = async (shippingAddress) => {
    setLoading(true);

    const billingZipCode = zipCodeFormValues?.zipCode;
    if (shippingAddress || billingZipCode) {
      try {
        const { transactionId } = await redeemGiftCode(
          {
            giftCode,
            shippingAddress,
            billingZipCode,
          },
          isUserUnmigrated
        );
        const analytics = giftData?.analytics;

        await getEntitlements();
        trackEvent(TRACKING_ENUMS.EVENT_REDEEM_COMPLETED, {
          page_order_id: transactionId,
          page_plan_name: analytics?.planName,
          page_plan_price: analytics?.planPrice,
          page_plan_id: analytics?.planId,
        });
        if (isUserUnmigrated) {
          navigate(giftCardRedeemConfirmation);
        } else {
          await redirectToFoxoneSubsPath({ path: 'giftcard-redeem-confirmation' });
        }
      } catch (error) {
        setLoading(false);
        return onSubmitReactFormError(error);
      }
    }
  };

  return (
    <CenterFrameLayout title="Redeem Your Gift Code" showLoader={showPageLoader} customClass="redeemGiftCardContainer">
      {isTrialistUser && (
        <div className="trialistMsgWrapper">
          <RedeemGiftTrialistFinePrint className="noRedeemMsg" />
          <SmartLink className="redeemWatchNowBtn" text="Watch Fox Nation" linkUrl={rootDomain} reload />
        </div>
      )}
      {isIAPUser && <RedeemGiftIAPFinePrint className="noRedeemMsg" />}
      <div className="centerForm">
        <RedeemGiftCardForm
          isDisabled={isTrialistUser || isIAPUser}
          onFormSubmit={handleGiftCodeSubmit}
          onChangeGiftCode={() => setGiftData(null)}
        />
        {!giftData && <RedeemGiftFinePrint className="disclaimer" />}
        <GiftCardInfo giftPlan={giftData?.giftPlan} />
        {showShippingForm && (
          <div className="redeemYouGiftForm">
            <div className="redeemYouGiftFormTitle">Where should we mail your gift merchandise?</div>
            <ShippingForm onFormSubmit={onSubmitToRedeem} isLoading={isLoading} />
          </div>
        )}
        {showZipCodeForm && (
          <ZipCodeForm onFormSubmit={onSubmitToRedeem} isLoading={isLoading} setFormValues={setZipcodeFormValues} />
        )}
      </div>
    </CenterFrameLayout>
  );
};

async function verifyGiftCode(giftCode, isUserUnmigrated) {
  if (isUserUnmigrated) {
    const giftData = await verifyGiftCodeV1(giftCode);
    if (giftData && giftData.packageId) {
      const giftPlan = giftPlans[giftData.packageId];
      const analytics = {
        planName: giftPlan?.title || '',
        planPrice: parseFloat(giftData.productPrice),
        planId: giftData.productSku,
      };
      return {
        valid: true,
        hasMerchandise: giftData.hasMerchandise,
        giftPlan,
        analytics,
      };
    }
  } else {
    const giftData = await verifyGiftCodeV2(giftCode);
    if (giftData && giftData.durationDays) {
      const durationDays = giftData.durationDays;
      const title = giftData.product;
      let description = '';
      let imgSrc = '';
      if (durationDays >= 365) {
        const durationYears = Math.round(durationDays / 365);
        description = `${durationYears} Year subscription to FOX Nation`;

        if (durationYears === 1) {
          imgSrc = '/account/static/images/FN/gifts/D2C-Gift-1Yr-No-Merch.png';
        } else {
          if (durationYears === 2) {
            imgSrc = '/account/static/images/FN/gifts/D2C-Gift-2Yr-No-Merch.png';
          }
        }
      } else {
        description = `${durationDays} Day subscription to FOX Nation`;
      }

      const appServiceId = giftData.appServiceId;
      const analytics = {
        planName: title,
        planPrice: 0,
        planId: appServiceId,
      };

      return {
        valid: true,
        hasMerchandise: false,
        giftPlan: {
          title,
          description,
          imgSrc: {
            default: imgSrc,
            modal: imgSrc,
          },
        },
        analytics,
      };
    }
  }

  // This is just a fallback in case the API returns an unexpected valid response.
  throw 'Unexpected response. Please try again later.';
}

async function redeemGiftCode({ giftCode, shippingAddress, billingZipCode }, isUserUnmigrated) {
  if (isUserUnmigrated) {
    const { transactionId } = await redeemGiftV1({
      pin: giftCode,
      shippingAddress,
      billingZipCode,
    });
    return { transactionId };
  } else {
    const { purchaseId } = await redeemGiftV2({ giftCode, billingZipCode });
    return { transactionId: purchaseId };
  }
}
